import * as React from "react";

/**
 * Xyqar voice widget wrapper.
 *
 * Loads ElevenLabs Convai webcomponent on demand, then mounts <elevenlabs-convai>
 * configured with a per-tenant agent ID resolved server-side via /api/v1/voice/token.
 * The frontend never sees the ElevenLabs API key — only a short-lived signed url
 * + agent id are returned.
 *
 * Usage:
 *   <VoiceAgent
 *      apiBase="https://api.xyqar.com"
 *      tenantId={tenant?.id ?? null}
 *      onReady={(meta) => console.log(meta)}
 *   />
 */

const SCRIPT_SRC = "https://elevenlabs.io/convai-widget/index.js";

let _scriptLoading: Promise<void> | null = null;

export function preloadConvaiScript(): void {
  loadConvaiScript();
}

function loadConvaiScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (customElements.get("elevenlabs-convai")) return Promise.resolve();
  if (_scriptLoading) return _scriptLoading;
  _scriptLoading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src   = SCRIPT_SRC;
    s.async = true;
    s.type  = "text/javascript";
    // No crossOrigin: ElevenLabs CDN does not send Access-Control-Allow-Origin.
    // Loading as a regular script tag works fine without CORS.
    s.onload  = () => resolve();
    s.onerror = () => reject(new Error("convai script failed"));
    document.head.appendChild(s);
  });
  return _scriptLoading;
}

interface VoiceTokenResp {
  signed_url: string;
  agent_id:   string;
  expires_at: string;
  rate_remaining: number;
}

export interface VoiceAgentProps {
  apiBase:   string;                         // backend root (https://api.xyqar.com)
  tenantId?: string | null;                  // null on landing
  fetchTokenPath?: string;                   // override
  /** Resolved Auth0 access token — forwarded as Authorization: Bearer <token>.
   *  Required when the voice session is gated behind Auth0 login.
   *  Omit only on the public landing (unauthenticated ARIA widget). */
  authToken?: string | null;
  /** Persona for transcript persistence (which agent silo). */
  persona?:  "client" | "talent" | "partner" | "advisor" | "ambassador";
  /** Lead row id to attach transcript to (set after CRM lead created). */
  leadId?:   string | null;
  /** Override path for transcript persistence. */
  saveTranscriptPath?: string;
  onReady?:  (meta: VoiceTokenResp) => void;
  onError?:  (err: Error) => void;
  onTranscriptSaved?: (resp: { id: string; score_delta: number }) => void;
  /** Called when the conversation ends (convai-end event) — use to unblock submit. */
  onEnd?:    () => void;
  className?: string;
  height?:   number | string;
  /** Enable video/camera for the session. Default false. */
  videoEnabled?: boolean;
  /** Language override for ARIA responses. Default: English.
   *  Pass language="fr" explicitly for French sessions. */
  language?: string;
  /** Arbitrary metadata forwarded to the backend voice router. */
  metadata?: Record<string, unknown> | null;
}

interface TranscriptTurn { role: "agent" | "user"; text: string; ts?: number }

export const VoiceAgent: React.FC<VoiceAgentProps> = ({
  apiBase, tenantId, fetchTokenPath = "/api/v1/voice/token",
  authToken, persona, leadId, saveTranscriptPath = "/api/v1/voice/transcript",
  onReady, onError, onTranscriptSaved, onEnd, className, height = 560,
  videoEnabled = false,
  language,
  metadata,
}) => {
  // Language: explicit prop wins; default is English for all personas.
  // French must be explicitly set by the caller (language="fr").
  const lang = language ?? "en";
  const [meta, setMeta] = React.useState<VoiceTokenResp | null>(null);
  const [err,  setErr]  = React.useState<string | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const turnsRef = React.useRef<TranscriptTurn[]>([]);
  const startedAtRef = React.useRef<number>(Date.now());
  const savedRef = React.useRef<boolean>(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadConvaiScript();
        const headers: Record<string, string> = { "Content-Type": "application/json" };
        if (tenantId) headers["X-Xyqar-Tenant"] = tenantId;
        // Auth0 JWT — backend logs which authenticated user started the session.
        // The WAF edge-gate is bypassed when a valid Bearer token is present.
        if (authToken) headers["Authorization"] = `Bearer ${authToken}`;
        const r = await fetch(`${apiBase.replace(/\/$/, "")}${fetchTokenPath}`, {
          method: "POST", headers, body: JSON.stringify({ persona: persona ?? null }),
        });
        if (!r.ok) throw new Error(`voice token ${r.status}`);
        const j = (await r.json()) as VoiceTokenResp;
        if (cancelled) return;
        setMeta(j);
        startedAtRef.current = Date.now();
        onReady?.(j);
      } catch (e) {
        const er = e as Error;
        if (cancelled) return;
        setErr(er.message);
        onError?.(er);
      }
    })();
    return () => { cancelled = true; };
  }, [apiBase, tenantId, fetchTokenPath, persona, onReady, onError]);

  // Buffer convai messages; flush to backend on session end / unmount.
  const flush = React.useCallback(async () => {
    if (savedRef.current) return;
    if (!persona) return;                                  // no silo → no persist
    if (turnsRef.current.length === 0) return;
    savedRef.current = true;
    try {
      const txHeaders: Record<string, string> = { "Content-Type": "application/json" };
      if (authToken) txHeaders["Authorization"] = `Bearer ${authToken}`;
      const r = await fetch(`${apiBase.replace(/\/$/, "")}${saveTranscriptPath}`, {
        method: "POST",
        headers: txHeaders,
        body: JSON.stringify({
          lead_id:    leadId ?? null,
          persona,
          agent_id:   meta?.agent_id ?? "",
          duration_s: Math.round((Date.now() - startedAtRef.current) / 1000),
          transcript: turnsRef.current,
        }),
      });
      if (r.ok) {
        const j = await r.json();
        onTranscriptSaved?.({ id: j.id, score_delta: j.score_delta });
      }
    } catch { /* swallow — best-effort */ }
  }, [apiBase, saveTranscriptPath, persona, leadId, meta, onTranscriptSaved]);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container || !meta) return;
    const onMsg = (ev: Event) => {
      const d = (ev as CustomEvent).detail || {};
      const role: "agent" | "user" =
        d.source === "user" || d.role === "user" ? "user" : "agent";
      const text: string = d.message || d.text || d.content || "";
      if (text) {
        turnsRef.current.push({
          role, text,
          ts: (Date.now() - startedAtRef.current) / 1000,
        });
      }
    };
    const handleEnd = () => { void flush(); onEnd?.(); };
    const MSG = ["convai-message", "elevenlabs-convai:message"];
    const END = ["convai-end", "elevenlabs-convai:end", "elevenlabs-convai:call"];
    const bind = (t: Element) => { MSG.forEach(e => t.addEventListener(e, onMsg)); END.forEach(e => t.addEventListener(e, handleEnd)); };
    const unbind = (t: Element) => { MSG.forEach(e => t.removeEventListener(e, onMsg)); END.forEach(e => t.removeEventListener(e, handleEnd)); };
    bind(container);
    const widget = container.querySelector("elevenlabs-convai");
    if (widget) bind(widget);
    const obs = new MutationObserver((muts) => {
      for (const m of muts) for (const n of Array.from(m.addedNodes))
        if (n instanceof Element && n.tagName.toLowerCase() === "elevenlabs-convai") bind(n);
    });
    obs.observe(container, { childList: true, subtree: true });
    return () => {
      obs.disconnect();
      const w = container.querySelector("elevenlabs-convai");
      if (w) unbind(w);
      unbind(container);
      void flush();
    };
  }, [meta, flush, onEnd]);

  if (err) {
    return (
      <div className={className} role="alert"
           style={{ padding: 16, border: "1px solid var(--xyq-error,#E24A4A)", borderRadius: 12 }}>
        <strong style={{ display: "block", marginBottom: 6 }}>ARIA unavailable</strong>
        <p style={{ marginBottom: 12, fontSize: 13, opacity: 0.7 }}>{err}</p>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => { setErr(null); savedRef.current = false; }}
            style={{ fontSize: 12, padding: "6px 14px", borderRadius: 8,
                     border: "1px solid rgba(255,255,255,0.2)", background: "transparent",
                     color: "white", cursor: "pointer" }}
          >
            Retry
          </button>
          <button
            onClick={() => { setErr(null); onEnd?.(); }}
            style={{ fontSize: 12, padding: "6px 14px", borderRadius: 8,
                     border: "1px solid rgba(255,255,255,0.1)", background: "transparent",
                     color: "rgba(255,255,255,0.5)", cursor: "pointer" }}
          >
            Skip — submit without ARIA
          </button>
        </div>
      </div>
    );
  }
  if (!meta) {
    return (
      <div className={className}
           style={{ height, display: "grid", placeItems: "center", opacity: 0.6 }}>
        Loading ARIA…
      </div>
    );
  }
  // ElevenLabs convai webcomponent — typed loosely as it's not in React JSX namespace.
  // The widget has no HTML attribute for video — video mode must be enabled in the
  // ElevenLabs dashboard per-agent (agent > Settings > Video call). The videoEnabled
  // prop is kept for forward-compat; we pass override-config once ElevenLabs exposes it.
  const Tag: any = "elevenlabs-convai";
  return (
    <div ref={containerRef} className={className} style={{ minHeight: height }}>
      <Tag
        agent-id={meta.agent_id}
        signed-url={meta.signed_url}
        variant="full"
        language={lang}
      />
    </div>
  );
};
